import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PageTransition from './PageTransition';

const NAV = [
  { to: '/dashboard',          label: 'Dashboard',       group: 'OVERVIEW',    icon: 'space_dashboard' },
  { to: '/shipments/pending',  label: 'Pending',         group: 'MANAGEMENT',  icon: 'inventory_2' },
  { to: '/shipments/by-customer', label: 'By Customer',  group: 'MANAGEMENT',  icon: 'pending_actions' },
  { to: '/shipments/assigned', label: 'Assigned',        group: 'MANAGEMENT',  icon: 'flight_takeoff' },
  { to: '/flights',            label: 'Flights',         group: 'MANAGEMENT',  icon: 'flight' },
  { to: '/customers',          label: 'Customers',       group: 'MANAGEMENT',  icon: 'group' },
  { to: '/register-codes',     label: 'Register Codes',  group: 'MANAGEMENT',  icon: 'fact_check' },
  { to: '/find-codes',         label: 'Find Codes',      group: 'MANAGEMENT',  icon: 'search' },
  { to: '/my-account',         label: 'My Account',      group: 'SETTINGS',    icon: 'account_circle' },
  { to: '/accounts',           label: 'Accounts',        group: 'SETTINGS',    icon: 'admin_panel_settings', roles: ['superadmin'] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate('/login'); };
  const groups = [...new Set(NAV.map(n => n.group))];

  return (
    <div style={{display:'flex',minHeight:'100vh'}}>
      <style>{`
        .kdx-sidebar {
          width: 240px;
          background: var(--sidebar-bg);
          border-right: 1px solid var(--sidebar-border);
          display: flex;
          flex-direction: column;
          position: fixed;
          top: 0; bottom: 0; left: 0;
          overflow-y: auto;
          z-index: 20;
        }
        .kdx-logo-block {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 4px;
          padding: 16px 20px;
          background: #ffffff;
          border-bottom: 1px solid rgba(0, 0, 0, 0.08);
          min-height: 64px;
        }
        .kdx-logo-img {
          height: 32px;
          width: auto;
          display: block;
        }
        .kdx-sub {
          font-size: 10px;
          font-weight: 600;
          color: #4b5563;
          letter-spacing: 0.12em;
          text-transform: lowercase;
        }
        .kdx-nav { padding: 12px 0; flex: 1; }
        .kdx-nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 0 8px;
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          color: var(--sidebar-text-secondary);
          text-decoration: none;
          transition: background-color var(--transition-fast), color var(--transition-fast);
        }
        .kdx-nav-item:hover {
          background: rgba(255, 255, 255, 0.03);
          color: var(--sidebar-text);
        }
        .kdx-nav-item.active {
          background: var(--sidebar-surface);
          color: var(--sidebar-text);
          font-weight: 600;
          box-shadow: inset 3px 0 0 var(--accent);
        }
        .kdx-nav-item .material-symbols-outlined {
          font-size: 18px;
          color: var(--sidebar-text-muted);
          transition: color var(--transition-fast);
        }
        .kdx-nav-item.active .material-symbols-outlined {
          color: var(--accent);
          font-variation-settings: 'FILL' 1, 'wght' 500, 'GRAD' 0, 'opsz' 24;
        }
        .kdx-user-section {
          padding: 12px;
          border-top: 1px solid var(--sidebar-border);
          margin-top: auto;
        }
        .kdx-user-card {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px;
          margin-bottom: 8px;
        }
        .kdx-avatar {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: var(--accent);
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 700;
          flex-shrink: 0;
        }
        .kdx-user-name {
          font-size: 13px;
          font-weight: 600;
          color: var(--sidebar-text);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .kdx-user-role {
          font-size: 10px;
          color: var(--sidebar-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.12em;
          margin-top: 2px;
        }
        .kdx-logout-btn {
          width: 100%;
          background: transparent;
          border: 1px solid var(--sidebar-border-strong);
          border-radius: 6px;
          padding: 7px 12px;
          font-size: 12px;
          font-weight: 500;
          color: var(--sidebar-text-secondary);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-family: var(--font-sans);
          transition: all var(--transition-fast);
        }
        .kdx-logout-btn:hover {
          background: var(--sidebar-elevated);
          border-color: rgba(255, 255, 255, 0.18);
          color: var(--sidebar-text);
        }
        .kdx-logout-btn .material-symbols-outlined { font-size: 16px; }
        .kdx-main {
          margin-left: 240px;
          flex: 1;
          padding: 32px 36px;
          min-height: 100vh;
          background: var(--bg-primary);
        }
      `}</style>

      <aside className="kdx-sidebar">
        <div className="kdx-logo-block">
          <img src="/logo.png" alt="KDExpress" className="kdx-logo-img" />
          <div className="kdx-sub">checkout.hub</div>
        </div>

        <nav className="kdx-nav">
          {groups.map(group => {
            const items = NAV.filter(n => n.group === group && (!n.roles || n.roles.includes(user?.role)));
            if (!items.length) return null;
            return (
              <div key={group}>
                <div className="sidebar-section-label">{group}</div>
                {items.map(item => (
                  <NavLink key={item.to} to={item.to} className={({isActive}) => `kdx-nav-item${isActive?' active':''}`}>
                    <span className="material-symbols-outlined">{item.icon}</span>
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="kdx-user-section">
          <div className="kdx-user-card">
            <div className="kdx-avatar">{user?.name?.[0]?.toUpperCase()}</div>
            <div style={{minWidth:0}}>
              <div className="kdx-user-name">{user?.name}</div>
              <div className="kdx-user-role">{user?.role}</div>
            </div>
          </div>
          <button className="kdx-logout-btn" onClick={handleLogout}>
            <span className="material-symbols-outlined">logout</span>
            Sign out
          </button>
        </div>
      </aside>

      <main className="kdx-main"><PageTransition>{children}</PageTransition></main>
    </div>
  );
}
